import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Zap, Search, Loader2, CheckCircle, Package } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { dualPriceText } from "@/components/DualPrice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getCurrentWeek = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const days = Math.floor((now - start) / (24 * 60 * 60 * 1000));
  return Math.ceil((days + start.getDay() + 1) / 7);
};

export default function SpecialOrderDialog({ open, onOpenChange, suppliers = [], onCreated }) {
  const currentWeek = getCurrentWeek();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [product, setProduct] = useState(null);
  const [priceMap, setPriceMap] = useState({});
  const [saving, setSaving] = useState(false);
  const [productTax, setProductTax] = useState(0);
  const [form, setForm] = useState({
    supplier_id: "", unit_price: "", currency: "SEK", chemical_tax: "", quantity: 1, expected_week: String(currentWeek)
  });

  useEffect(() => {
    if (!open) {
      setSearchQuery(""); setSearchResults([]); setProduct(null); setPriceMap({});
      setForm({ supplier_id: "", unit_price: "", currency: "SEK", chemical_tax: "", quantity: 1, expected_week: String(currentWeek) });
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (!open || product) return;
    if (searchQuery.trim().length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/inventory`, { params: { search: searchQuery.trim(), limit: 10 } });
        setSearchResults(res.data.items || []);
      } catch { /* ignore */ } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, open, product]);

  const selectProduct = async (p) => {
    setProduct(p);
    setSearchResults([]);
    setSearchQuery(p.artikelnummer || p.ean);
    try {
      const res = await axios.get(`${API}/purchasing/prices/by-ean/${p.ean}`);
      const priced = (res.data?.prices || []).filter(x => (x.purchase_price || 0) > 0 && !x.unavailable);
      const map = {};
      priced.forEach(x => { map[x.supplier_id] = { price: x.purchase_price, currency: x.currency || "SEK", chemical_tax: x.chemical_tax || 0 }; });
      setPriceMap(map);
      // Preselect cheapest supplier with a price
      const cheapest = priced.slice().sort((a, b) => (a.purchase_price || 0) - (b.purchase_price || 0))[0];
      let tax = 0;
      try { tax = (await axios.get(`${API}/purchasing/product-chemical-tax/${p.ean}`)).data?.chemical_tax || 0; } catch { /* */ }
      setProductTax(tax);
      if (cheapest) {
        setForm(prev => ({
          ...prev,
          supplier_id: cheapest.supplier_id,
          unit_price: String(cheapest.purchase_price),
          currency: cheapest.currency || "SEK",
          chemical_tax: cheapest.chemical_tax ? String(cheapest.chemical_tax) : (tax ? String(tax) : "")
        }));
      } else if (tax) {
        setForm(prev => ({ ...prev, chemical_tax: String(tax) }));
      }
    } catch { /* ignore */ }
  };

  const onSupplierChange = (sid) => {
    const listed = priceMap[sid];
    const supCur = suppliers.find(s => s.id === sid)?.default_currency || "SEK";
    setForm(prev => ({
      ...prev,
      supplier_id: sid,
      // Clear the price when the chosen supplier has no prislista price (must be typed)
      unit_price: listed?.price != null ? String(listed.price) : "",
      currency: listed?.currency || supCur,
      chemical_tax: listed?.chemical_tax ? String(listed.chemical_tax) : (productTax ? String(productTax) : "")
    }));
  };

  const resetProduct = () => { setProduct(null); setSearchQuery(""); setPriceMap({}); };

  const handleSubmit = async () => {
    if (!product) { toast.error("Välj en produkt"); return; }
    if (!form.supplier_id) { toast.error("Välj leverantör"); return; }
    if (!form.unit_price || parseFloat(form.unit_price) <= 0) { toast.error("Ange pris"); return; }
    if (form.quantity < 1) { toast.error("Ange antal"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/purchasing/orders/special`, {
        ean: product.ean,
        artikelnummer: product.artikelnummer,
        quantity: parseInt(form.quantity),
        supplier_id: form.supplier_id,
        unit_price: parseFloat(form.unit_price),
        currency: form.currency,
        chemical_tax: form.chemical_tax ? parseFloat(form.chemical_tax) : null,
        expected_delivery_week: form.expected_week ? parseInt(form.expected_week) : null
      });
      toast.success(`Specialorder skapad: ${form.quantity} st ${product.artikelnummer} – markerad som beställd`);
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte skapa specialorder");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-500" />
            Specialorder
          </DialogTitle>
          <DialogDescription>
            Hoppa över förfrågan – välj produkt, bekräfta pris och leverantör och beställ direkt.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Product search / selected */}
          {!product ? (
            <div className="space-y-2">
              <Label>Produkt</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  autoFocus
                  className="pl-10"
                  placeholder="Sök artikelnummer eller EAN..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="special-order-search"
                />
                {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
              </div>
              {searchResults.length > 0 && (
                <div className="max-h-56 overflow-auto border rounded-md divide-y">
                  {searchResults.map(p => (
                    <button
                      key={p.ean}
                      className="w-full text-left px-3 py-2 hover:bg-muted flex items-center gap-2"
                      onClick={() => selectProduct(p)}
                      data-testid={`special-order-result-${p.ean}`}
                    >
                      <Package className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="min-w-0">
                        <span className="font-medium block truncate">{p.artikelnummer || p.ean}</span>
                        <span className="text-xs text-muted-foreground block truncate">{p.produktnamn || ""} · {p.ean}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between border rounded-md p-3 bg-muted/30">
              <div className="min-w-0">
                <p className="font-medium truncate">{product.artikelnummer || product.ean}</p>
                <p className="text-xs text-muted-foreground truncate">{product.produktnamn || ""} · {product.ean}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={resetProduct} data-testid="special-order-change-product">Byt</Button>
            </div>
          )}

          {product && (
            <>
              <div className="space-y-2">
                <Label>Leverantör *</Label>
                <Select value={form.supplier_id} onValueChange={onSupplierChange}>
                  <SelectTrigger data-testid="special-order-supplier"><SelectValue placeholder="Välj leverantör..." /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.company_name}{priceMap[s.id] ? ` – ${dualPriceText(priceMap[s.id].price, priceMap[s.id].currency)}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Pris *</Label>
                  <Input type="number" step="0.01" min="0" placeholder="0" value={form.unit_price}
                    onChange={(e) => setForm(prev => ({ ...prev, unit_price: e.target.value }))} data-testid="special-order-price" />
                </div>
                <div className="space-y-2">
                  <Label>Valuta</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm(prev => ({ ...prev, currency: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SEK">SEK</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="DKK">DKK</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>Antal *</Label>
                  <Input type="number" min="1" value={form.quantity}
                    onChange={(e) => setForm(prev => ({ ...prev, quantity: e.target.value }))} data-testid="special-order-quantity" />
                </div>
                <div className="space-y-2">
                  <Label>Kem. skatt</Label>
                  <Input type="number" step="0.01" min="0" placeholder="0" value={form.chemical_tax}
                    onChange={(e) => setForm(prev => ({ ...prev, chemical_tax: e.target.value }))} data-testid="special-order-chemtax" />
                </div>
                <div className="space-y-2">
                  <Label>Vecka</Label>
                  <Select value={form.expected_week} onValueChange={(v) => setForm(prev => ({ ...prev, expected_week: v }))}>
                    <SelectTrigger data-testid="special-order-week"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {Array.from({ length: 12 }, (_, i) => {
                        let w = currentWeek + i; if (w > 52) w -= 52;
                        return <SelectItem key={i} value={w.toString()}>v.{w}{i === 0 && " (nu)"}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !product || !form.supplier_id || !(parseFloat(form.unit_price) > 0) || !(parseInt(form.quantity) >= 1)}
            className="gap-2 bg-amber-500 hover:bg-amber-600 text-black"
            data-testid="special-order-submit"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
            Markera som beställd
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
